import React, { useState } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { ToastProvider } from './components/Toast';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import Services from './pages/Services';
import Enquiries from './pages/Enquiries';
import DemoSchedules from './pages/DemoSchedules';
import Settings from './pages/Settings';
import Testimonials from './pages/Testimonials';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import AlertBox from './components/AlertBox';
import './index.css';

/**
 * ProtectedRoute — renders children only when a valid session exists.
 *
 * Auth state is stored in sessionStorage (tab-isolated), so:
 *  - Tab 1 logs in  → sessionStorage has the user  → authenticated
 *  - Tab 2 opens fresh URL → sessionStorage is empty → bounced to login
 *
 * Note: "Duplicate Tab" copies sessionStorage by browser design, so a
 * duplicated tab stays authenticated. That is expected behaviour and
 * cannot be prevented client-side without a server-side session check.
 */
function ProtectedRoute({ children }) {
  const { currentUser } = useApp();

  // Check sessionStorage directly as a second layer — guards against the
  // edge case where React state hydrates before the effect runs.
  const sessionValid = !!sessionStorage.getItem('ms_user');

  if (!currentUser || !sessionValid) {
    return <AuthPage />;
  }

  return children;
}

function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);

  // ── Persist active page across browser refresh ──────────────────────────
  const [activePage, setActivePage] = useState(
    () => sessionStorage.getItem('ms_active_page') || 'dashboard'
  );

  // Track how many times each page has been navigated to — used as refreshKey
  const [refreshKeys, setRefreshKeys] = useState({});

  const handleNavigate = (page) => {
    sessionStorage.setItem('ms_active_page', page);
    // Increment the refresh key for the target page so it re-fetches data
    setRefreshKeys(prev => ({ ...prev, [page]: (prev[page] || 0) + 1 }));
    setActivePage(page);
  };

  const pages = {
    dashboard:    <Dashboard    key={refreshKeys.dashboard}    refreshKey={refreshKeys.dashboard}    onNavigate={handleNavigate} />,
    services:     <Services     key={refreshKeys.services}     refreshKey={refreshKeys.services} />,
    enquiries:    <Enquiries    key={refreshKeys.enquiries}    refreshKey={refreshKeys.enquiries} />,
    demos:        <DemoSchedules key={refreshKeys.demos}       refreshKey={refreshKeys.demos} />,
    testimonials: <Testimonials key={refreshKeys.testimonials} refreshKey={refreshKeys.testimonials} />,
    settings:     <Settings     key={refreshKeys.settings}     refreshKey={refreshKeys.settings} />,
  };

  return (
    <div className="app-layout">
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(v => !v)}
        activePage={activePage}
        onNavigate={handleNavigate}
      />
      <div className={`main-content${collapsed ? ' collapsed' : ''}`}>
        <Header activePage={activePage} onNavigate={handleNavigate} onToggle={() => setCollapsed(v => !v)} />
        {pages[activePage] || <Dashboard />}
      </div>
      <AlertBox />
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <ToastProvider>
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      </ToastProvider>
    </AppProvider>
  );
}
